const notificationStore = useNotificationStore();

export default defineNuxtRouteMiddleware(async (to, from) => {
  const pageContentStore = usePageContentStore();
  const userStore = useUserStore();
  const isUser = userStore.isUserLoggedIn;

  // Only refresh static content like footers, blocks, etc after 15 minutes.
  let sectionContent = pageContentStore.sectionContent;
  const lastUpdated = pageContentStore.lastUpdated;
  const now = new Date();

  if (now.getTime() > lastUpdated) {
    const querySectionContent = `
      {
        pageContent(
          where:{
            OR:[
              { column:SLUG, value: "footer"},
              { column:SLUG, value: "login-videos"}
            ]
          }
        ) {
          slug
          content
        }
      }
    `;

    try {
      const contentResult = await getQuery(querySectionContent);
      sectionContent = contentResult.pageContent;

      if (sectionContent) {
        pageContentStore.setSectionContent({ sectionContent });

        // Add 15 minutes to the current time and store it.
        now.setMinutes(now.getMinutes() + 15);
        pageContentStore.setLastUpdated(now.getTime());
      }
    } catch (e) {
      notificationStore.error({
        variant: "error",
        messageHead: "Error fetching section content:",
        messageBody: String(e)
      });
    }
  }

  let query = `
    {
      tags(
        first: 200,
        minFilesCount: 0,
        where:{
          AND:[
            { column:IS_ENABLED, value: true }
          ]
        }
      ) {
        data {
          id
          name
          slug
          filesCount
        }
      }
      contentTypes(
        minFilesCount: 1,
        where:{
          AND:[
            { column:IS_ENABLED, value: true },
            { column:IS_IN_NAV, value: true}
          ]
        }
      ) {
        data {
          id
          name
          slug
          coverSvg
          filesCount
        }
      }
      topics (
        where: {
          AND: [
            { column: IS_ENABLED, value: true }
          ]
        }
      ) {
        data {
          id
          name
          slug
          coverSvg,
          filesCount
        }
      }
      pageContentBySlug(slug: "${String(to.name)}") {
        id,
        title,
        slug,
        content,
        sideblocks {
          id
          slug
          title
          content
          url
          region
          image {
            id
            path
          }
        }
      }
      userPlans {
        id
        label
        short_description
        slug
        type
        is_visible
        is_alumni
        renewal_days_before
        renewal_days_after
        alumniOfferedPlan {
          id
        }
        cover {
          path
        }
        coverSvg
        pivot {
          valid_from
          valid_to
        }
        plans {
          id
          label
          description
          amount
          billing_scheme
          interval
          interval_count
          currency
          renewal_amount
          tax
        }
        features {
          name
          pivot {
            is_new
            is_active
            position
          }
        }
        files { 
            id 
            title 
        }
      }
    }`;

  if (!isUser) {
    query = `
    {
      pageContentBySlug(slug: "${String(to.name)}") {
        id,
        title,
        slug,
        content
      }
    }`;
  }
  const results = await getQuery(query);

  let tags, contentTypes, topics, pageContent: PageContent, userPlans: Plan[];

  try {
    pageContent = results.pageContentBySlug;
    if (pageContent) {
      pageContentStore.setCurrentPage({ pageContent });
    }
  } catch (e) {
    notificationStore.error({
      variant: "error",
      messageHead: "Error fetching page content:",
      messageBody: String(e)
    });
  }

  if (isUser) {
    try {
      userPlans = results.userPlans;

      userPlans.sort((a: any, b: any) => {
        const dateA = new Date(a.pivot.valid_to).getTime();
        const dateB = new Date(b.pivot.valid_to).getTime();
        return dateB - dateA;
      });

      userStore.updateUserPlans({ userPlans });
    } catch (e) {
      notificationStore.error({
        variant: "error",
        messageHead: "Error fetching user plans:",
        messageBody: String(e)
      });
    }

    try {
      const tags: Tag[] = (results.tags?.data ?? []).filter(
        (type: Tag) => type.filesCount > 0
      );
      pageContentStore.setTags(tags);
    } catch (e) {
      notificationStore.error({
        variant: "error",
        messageHead: "Error fetching tags content:",
        messageBody: String(e)
      });
    }

    try {
      const contentTypes: ContentType[] = (
        results.contentTypes?.data ?? []
      ).filter((type: ContentType) => type.filesCount > 0);
      pageContentStore.setContentTypes(contentTypes);
    } catch (e) {
      notificationStore.error({
        variant: "error",
        messageHead: "Error fetching contents:",
        messageBody: String(e)
      });
    }

    try {
      const topics: Topic[] = (results.topics?.data ?? []).filter(
        (type: Topic) => type.filesCount > 0
      );
      if (topics) {
        pageContentStore.setTopics(topics);
      }
    } catch (e) {
      notificationStore.error({
        variant: "error",
        messageHead: "Error fetching topics:",
        messageBody: String(e)
      });
    }
  }
});
